import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { TitleText } from "../../../ui/Text";
import Table from "../../../ui/Table";
import TableFormatter, {
  EditDeleteFormatter,
} from "../../../ui/TableFormatter";
import Modal from "../../../ui/Modal";
import AddPersonForm from "./AddPersonForm";
import { GetHook } from "../../../api/api";
import {
  COMPANY_URL,
  ORGANISATION_URL,
  ROLE_URL,
  USER_URL,
} from "../../../api/urls";
import axios from "../../../utils/axios";
import { useLocation } from "react-router-dom";
import { get } from "../../../utils/get";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { parseDropdown } from "../../../utils/parseDropdown";
import useDebounce from "../../../utils/useDebounce";

const PeopleSettings = () => {
  const [isModalOpen, setisModalOpen] = useState(false);
  const [handleSubmit, setHandleSubmit] = useState();
  const [editData, setEditData] = useState({});
  const [edit, setEdit] = useState(false);
  const [users, setUsers] = useState<any>([]);
  const [filter, setFilter] = useState({});
  const [dataSize, setDataSize] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filterEndpoint, setFilterEndpoint] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const { data, error, pending, reload, availableResultSet } = GetHook(
    `${USER_URL}?limit=1`
  );
  const { data: companies } = GetHook(`${COMPANY_URL}`);
  const { data: roles } = GetHook(`${ROLE_URL}`);
  const { data: organisations } = GetHook(ORGANISATION_URL);
  const [pageCount, setPageCount] = useState(0);

  const location = useLocation();

  // useEffect(() => {
  //   setUsers(data);
  //   return () => {};
  // }, [data]);

  useEffect(() => {
    if (get(filter, "organisation") && get(filter, "company")) {
      setFilterEndpoint(
        `${get(filter, "organisation")}&${get(filter, "company")}`
      );
    } else if (get(filter, "organisation")) {
      setFilterEndpoint(`${get(filter, "organisation")}`);
    } else if (get(filter, "company")) {
      setFilterEndpoint(`${get(filter, "company")}`);
    } else {
      setFilterEndpoint("");
    }
    return () => {};
  }, [filter]);

  const handleEdit: any = (row: any) => {
    setisModalOpen(true);
    setEdit(true);
    setEditData(row);
    reload(get(location, "key"));
  };

  const handleDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.value) {
        if (id) {
          axios
            .delete(`${USER_URL}/${id}`)
            .then((res) => {
              setUsers(users.filter((user: any) => user.id != id));
              toast.success("User has been deleted");
              reload(get(location, "key"));
            })
            .catch((error) => toast.error(error.response.data.message));
        }
      }
    });
  };

  const closeModal = () => {
    setEdit(false);
    setEditData({});
    setisModalOpen(false);
    reload(get(location, "key"));
  };

  const paginationIdRef = useRef(0);

  const handlePagination = useCallback(
    ({ pageSize, pageIndex }) => {
      console.log(pageCount, pageSize, pageIndex);

      const paginationId = ++paginationIdRef.current;
      if (paginationId === paginationIdRef.current) {
        setPageCount(Math.ceil(availableResultSet / pageSize));
      }
      const currentIndex = pageIndex * pageSize;

      axios
        .get(
          filterEndpoint
            ? `${USER_URL}?limit=10&offset=${currentIndex}&${filterEndpoint}`
            : `${USER_URL}?limit=10&offset=${currentIndex}`
        )
        .then((res) => {
          setUsers(res?.data?.returned_resultset);
          setDataSize(res?.data.available_resultset_size);
          if (filterEndpoint) {
            setPageCount(
              Math.ceil(res?.data.available_resultset_size / pageSize)
            );
          }
        });
    },
    [availableResultSet, filterEndpoint]
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  useEffect(() => {
    // Make sure we have a value (user has entered something in input)
    if (debouncedSearchTerm) {
      // Set isSearching state
      setIsSearching(true);
      // Fire off our API call
      axios.get(`${USER_URL}?names=${debouncedSearchTerm}`).then((res) => {
        // Set back to false since request finished
        setIsSearching(false);
        // Set results state
        setFilteredUsers(res?.data?.returned_resultset);
        setDataSize(res?.data.available_resultset_size);
        setPageCount(Math.ceil(res?.data.available_resultset_size / 10));
      });
    } else {
      setResults([]);
    }
  }, [debouncedSearchTerm]);

  const handleSearch = (e) => {
    if (e?.target.value) {
      setSearchTerm(e.target.value);
      setFilter({ company: "", organisation: "" });
    } else {
      setFilteredUsers([]);
      setDataSize(0);
      setPageCount(Math.ceil(availableResultSet / 10));
      // console.log({ searchTerm });
    }
  };

  const columns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row }: any) => (
        <TableFormatter
          name={`${row.original.first_name} ${row.original.last_name}`}
          url={row.original.avatar_url}
          email={row.original.email}
          hasEmail
        />
      ),
    },
    {
      Header: "Job Title",
      accessor: "job_title",
    },
    {
      Header: "Organization",
      accessor: "organisation.name",
    },
    {
      Header: "Role",
      accessor: "role.name",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) =>
        row.original.active === true ? (
          <Status color="#13C997">Active</Status>
        ) : (
          <Status color="#FF6666">Non-Active</Status>
        ),
    },
    {
      id: "1",
      Header: "",
      Cell: ({ row }: any) => (
        <EditDeleteFormatter
          showEdit={() => handleEdit(row.original)}
          handleDelete={() => handleDelete(row.original.id)}
        />
      ),
    },
  ];
  return (
    <PeopleSettingsContainer>
      <HeaderContainer>
        <TitleText>People</TitleText>
      </HeaderContainer>
      <Table
        data={filteredUsers.length ? filteredUsers : users}
        columns={columns}
        dataSize={dataSize ? dataSize : availableResultSet}
        handlePagination={handlePagination}
        handleSearch={handleSearch}
        setFilter={setFilter}
        filter={filter}
        pageCount={pageCount}
        companies={parseDropdown(companies)}
        setModal={setisModalOpen}
        organisations={parseDropdown(organisations)}
      />
      {isModalOpen && (
        <Modal
          title={edit ? "Edit person" : "Add new person"}
          handleSubmit={handleSubmit}
          closeModal={closeModal}
        >
          <AddPersonForm
            closeModal={closeModal}
            setHandleSubmit={setHandleSubmit}
            editData={editData}
            users={users}
            setUsers={setUsers}
          />
        </Modal>
      )}
    </PeopleSettingsContainer>
  );
};

export default PeopleSettings;

const PeopleSettingsContainer = styled.div`
  display: grid;
  grid-template-rows: 5em auto;
  background-color: #fff;
`;
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.3em;
  border-bottom: 1px solid rgba(55, 81, 255, 0.1);
`;

const Status = styled.span`
  color: ${(props) => props.color};
  font-size: 12px;
`;
